import { useContext, useState } from 'react'
import { Trash2 } from 'lucide-react'
import {
  GlobalDataContext,
  TemplateContext,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Builder primitive — a one-click delete button with an inline confirmation
 * step, persisted through decoupled_toolbox's generic delete API.
 *
 * First click reveals « Confirmer ? Oui / Non » in place (no modal, no store
 * state). Confirming issues
 *
 *   POST /decoupled-api/delete?display=decoupled&entity_type=node
 *        &bundle=<row.type>&id=<row.id>
 *
 * then optimistically removes the row from the listing (`removeFrom`, matched by
 * `id`) and decrements the total counter (`totalLocation`) — no list refetch.
 * CSRF + credentials are injected by `fetchPatch.js` for `/decoupled-api/*`.
 *
 *   - type: DecoupledDeleteButton
 *     idLocation: ~.id                       # optional, default ~.id
 *     bundleLocation: ~.type                 # optional, default ~.type
 *     removeFrom: ~~.esResult.contents       # array to splice the deleted row from
 *     totalLocation: ~~.esResult.metadata.total
 */
export const DecoupledDeleteButton = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const resolve = (dataLocation, defaultValue = null) =>
    propsDataLocationToPathAndValue({
      currentPath: path,
      datafield,
      dataLocation,
      defaultValue,
      globalDataContext,
      templateContext,
    })

  const entityType = props.entityType ?? 'node'
  const display = props.display ?? 'decoupled'
  const deleteUrl = props.deleteUrl ?? '/decoupled-api/delete'

  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  const doDelete = () => {
    const { formData: id } = resolve(props.idLocation ?? '~.id')
    const { formData: bundle } = resolve(props.bundleLocation ?? '~.type')
    if (id == null || !bundle) {
      console.error('DecoupledDeleteButton: missing entity id or bundle, cannot delete.')
      return
    }

    const url =
      `${deleteUrl}?display=${encodeURIComponent(display)}` +
      `&entity_type=${encodeURIComponent(entityType)}` +
      `&bundle=${encodeURIComponent(bundle)}` +
      `&id=${encodeURIComponent(id)}`

    setBusy(true)
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
      })
      .then(() => {
        // Reset the confirm step BEFORE the optimistic removal: the Switch
        // reuses this component instance by index, so a lingering
        // `confirming` would otherwise bleed onto the row that shifts up.
        setConfirming(false)
        // Optimistic removal from the listing.
        if (props.removeFrom) {
          const { formData: list, formDataPath } = resolve(props.removeFrom)
          if (Array.isArray(list)) {
            globalDataContext.updateData(
              list.filter((it) => String(it?.id) !== String(id)),
              formDataPath,
            )
          }
        }
        if (props.totalLocation) {
          const { formData: total, formDataPath } = resolve(props.totalLocation)
          if (typeof total === 'number') {
            globalDataContext.updateData(Math.max(0, total - 1), formDataPath)
          }
        }
      })
      .catch((err) => {
        console.error('DecoupledDeleteButton: delete failed', err)
        setConfirming(false)
        // eslint-disable-next-line no-alert
        alert('La suppression a échoué.')
      })
      .finally(() => setBusy(false))
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        title="Supprimer"
        className={props.className ?? 'text-gray-300 hover:text-red-600 transition'}
      >
        <Trash2 size={16} />
      </button>
    )
  }

  // The Actions cell is narrow, so the confirm strip floats as an absolute
  // overlay anchored to the right of the row and extends leftward over the
  // « Créé le » / « Modifié le » columns (white background + shadow cover
  // them) instead of widening the column or being clipped. An invisible
  // trash icon keeps the cell footprint stable underneath.
  return (
    <span className="relative inline-flex items-center justify-end">
      <span className="invisible" aria-hidden="true">
        <Trash2 size={16} />
      </span>
      <span className="absolute right-0 top-1/2 -translate-y-1/2 z-10 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1 shadow-lg">
        <span className="text-xs text-gray-500">Confirmer ?</span>
        <button
          type="button"
          onClick={doDelete}
          disabled={busy}
          className="text-xs font-semibold px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >
          Oui
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
        >
          Non
        </button>
      </span>
    </span>
  )
}
